import { FLIGHT_STATUSES, JOURNEY_NOT_READY_FILTERS, JOURNEY_SORTS, TRAVEL_DIRECTIONS, TRAVEL_MODES } from "@seagames/contracts";
import { z } from "zod";
import { searchText } from "../../lib/list-page/list-search";

/**
 * The Journeys list's URL state (M9b §3), in the API's own keys. Every param
 * is lenient — a hand-edited value falls back to "not set" instead of an
 * error page. The detail route accepts the same keys so its back arrow
 * (`search: true`) restores the list exactly.
 *
 * Flags are `true` or absent. The router reads `?late=1` as the number 1 and
 * `?late=true` as `true`; both mean on.
 */
const lenient = <T extends z.ZodTypeAny>(schema: T) => schema.optional().catch(undefined);
const ymd = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const flag = z.preprocess((v) => (v === true || v === 1 || v === "1" || v === "true" ? true : undefined), z.literal(true).optional());
/** `?not_ready=1` arrives as the number 1. */
const notReady = z.preprocess((v) => (typeof v === "number" ? String(v) : v), z.enum(JOURNEY_NOT_READY_FILTERS));
/** Comma list of flight statuses, kept as typed; unknown members dropped, empty → not set. */
const statusList = z
	.string()
	.max(200)
	.transform((v) => {
		const known = v
			.split(",")
			.map((s) => s.trim())
			.filter((s) => (FLIGHT_STATUSES as readonly string[]).includes(s));
		return known.length > 0 ? [...new Set(known)].join(",") : undefined;
	});

export const journeysSearchShape = {
	games_id: lenient(z.string().uuid()),
	/** ARRIVAL when absent. */
	direction: lenient(z.enum(TRAVEL_DIRECTIONS)),
	from: lenient(ymd),
	to: lenient(ymd),
	mode: lenient(z.enum(TRAVEL_MODES)),
	entry_point_id: lenient(z.string().uuid()),
	responsible_organization_id: lenient(z.string().uuid()),
	status: lenient(statusList),
	q: lenient(searchText()),
	late: lenient(flag),
	not_ready: lenient(notReady),
	at_risk: lenient(flag),
	manual: lenient(flag),
	/** Full view only; the page ignores it for a NOC. */
	shared: lenient(flag),
	sort: lenient(z.enum(JOURNEY_SORTS)),
};
export const journeysSearch = z.object(journeysSearchShape);
export type JourneysSearch = z.output<typeof journeysSearch>;
